import {
  ArrowDownward,
  ArrowForward as ArrowForwardIcon,
  ChevronRight as ChevronRightIcon,
  ExpandMore as ExpandMoreIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography,
  useMediaQuery,
  useTheme,
  Modal
} from '@mui/material';
import * as React from 'react';
import { useLoadLeagueData } from '../../../../app/dataLoaderHooks';
import { useAppSelector } from '../../../../app/hooks';
import { League, LeaguePhase, Player, PlayerPoachInfo, RosterPositionEnum, Team, TradeProposalPlayer } from '../../../../sdk/model';
import LoadingModal from '../../../util/LoadingModal';
import type { PlayerRowData } from '../../../util/RosterUtils';
import {
  getActionButtons, getBenchData, getBid, getFilteredBenchData, getIrData, getPlayerInfo, getPlayersForTrade, getPsData, getStarterData, getSwapOption, isRequireConfirmation, playerLocked, SWAP_BUTTON_MAP, SwapOption,
  swapSpotsAfterConfirm
} from '../../../util/RosterUtils';
import { toSalary } from '../../../util/SalaryDisplay';
import { getMaxTeamSize, MIN_WEEKS_ON_IR } from '../../../util/TeamUtils';
import TradeDetails from '../../trade/TradeDetails';
import { getPlayerCurrentScoreFromStats } from "../../../util/ScoreService";
import PlayerInfoCard from '../../../cards/playerCard';
import playerMatchupDisplay from '../../../util/PlayerMatchupDisplay';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';

const ACTIVE_LEAGUE_PHASES = new Set([LeaguePhase.RegularSeason, LeaguePhase.Playoffs]);

interface TeamRosterMobileProps {
  leagueId: string;
  team: Team;
  playerMap: Map<string | undefined, Player | undefined>;
  currentSeasonYear: number;
  rosterSettings: Map<string | undefined, number | undefined>;
  myTeam: boolean;
}

const PositionSection = ({
  title,
  data,
  onPositionSelect,
  selectedPosition,
  selectedPlayerInfo,
  otherSelectedPosition,
  otherPlayerInfo,
  roster,
  playerMap,
  team,
  currentSeasonYear,
  playerPoachInfo,
  league,
  setRoster,
  setSelectedPosition,
  setOtherSelectedPosition,
  setErrors,
  myTeam
}: {
  title: string;
  data: any[];
  onPositionSelect: (position: string) => void;
  selectedPosition: string | undefined;
  selectedPlayerInfo: any;
  otherSelectedPosition: string | undefined;
  otherPlayerInfo: any;
  roster: any;
  playerMap: Map<string | undefined, Player | undefined>;
  team: Team;
  currentSeasonYear: number;
  playerPoachInfo: any[] | undefined;
  league: League | undefined;
  setRoster: (roster: any) => void;
  setSelectedPosition: (position: string | undefined) => void;
  setOtherSelectedPosition: (position: string | undefined) => void;
  setErrors: (errors: string[]) => void;
  myTeam: boolean;
}) => {
  const [expanded, setExpanded] = React.useState(true);
  const [viewingPlayer, setViewingPlayer] = React.useState(undefined)
  const [processing, setProcessing] = React.useState(false);
  const nflGames = useAppSelector((state) => state.nflGameDataLoad);
  const nflGameMap = new Map(nflGames.map(nflGame => [nflGame.id!, nflGame]));
  const theme = useTheme();
  
  const handlePositionClick = (playerInfo: PlayerRowData, isLocked: boolean | undefined) => {
    const otherPosition = playerInfo.id;
    if (selectedPosition) {
      // If we have a position already selected, try to swap
      const position1Info = selectedPosition.split('-');
      const position2Info = otherPosition.split('-');
      const swapOption = getSwapOption(position1Info, position2Info, roster, playerMap);
      
      if (swapOption !== SwapOption.NONE) {
        if (isRequireConfirmation(position1Info) || isRequireConfirmation(position2Info)) {
          setOtherSelectedPosition(otherPosition);
        } else {
          swapSpotsAfterConfirm(
            position1Info, 
            position2Info, 
            selectedPlayerInfo, 
            otherPlayerInfo, 
            team, 
            getBid(playerPoachInfo, selectedPlayerInfo?.playerId), 
            setRoster, 
            setSelectedPosition, 
            setOtherSelectedPosition, 
            setErrors,
            setProcessing,
            false
          );
        }
      }
    } else {
      if (otherPosition.startsWith(RosterPositionEnum.Ps) && !playerInfo.player || 
            isLocked) {
        // Ignore selects of empty practice squad positions and locked players
      } else {
        onPositionSelect(otherPosition);
      }
    }
  };


  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardHeader
        title={
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle1" fontWeight="bold" color="text.primary">
              {title}
            </Typography>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setExpanded(!expanded);
              }}
            >
              <ExpandMoreIcon
                sx={{
                  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: theme.transitions.create('transform', {
                    duration: theme.transitions.duration.shortest,
                  }),
                }}
              />
            </IconButton>
          </Box>
        }
        sx={{
          py: 1,
          px: 2,
          // bgcolor: theme.palette.grey[100],
          '& .MuiCardHeader-content': {
            width: '100%',
          },
        }}
      />
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <CardContent sx={{ p: 0 }}>
          <LoadingModal show={processing} />
          <Modal open={viewingPlayer !== undefined} onClose={() => setViewingPlayer(undefined)} 
              aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description"
              sx={{margin: '5%'}}>
            <PlayerInfoCard player={viewingPlayer} leagueId={league?.id}/>
          </Modal>
          <List disablePadding>
            {data?.map((playerInfoEntry: PlayerRowData, index) => {
              const player = playerInfoEntry.player;
              const teamPlayer = playerInfoEntry.teamPlayer;
              const isLocked = playerLocked(player, teamPlayer, playerInfoEntry.position, league);
              const isSelected = selectedPosition === playerInfoEntry.position;
              const isOtherSelected = otherSelectedPosition === playerInfoEntry.position;
              const currentWeekStats = player?.quick_stats?.currentWeeksStats;
              const nflGame = currentWeekStats ? nflGameMap.get(currentWeekStats.gameId!) : undefined;
              const swapPlayer = playerInfoEntry.autoSwapPlayer ? playerMap.get(playerInfoEntry.autoSwapPlayer) : undefined;

              const emptyPositionString = RosterPositionEnum.Bn !== playerInfoEntry.position ? playerInfoEntry.position + ' Empty' : 'Empty';
              let projection : number | undefined = undefined;
              if (player) {
                projection = getPlayerCurrentScoreFromStats(player, league?.currentSeason?.scoringSettings!, undefined).projectedScore;
              }
              return (
                <React.Fragment key={playerInfoEntry.id}>
                  <ListItem
                    button={myTeam}
                    selected={myTeam && (isSelected || isOtherSelected)}
                    sx={{
                      bgcolor: myTeam && isSelected 
                        ? theme.palette.action.selected 
                        : myTeam && isOtherSelected 
                          ? theme.palette.action.hover 
                          : 'inherit',
                      '&:hover': myTeam ? {
                        bgcolor: theme.palette.action.hover,
                      } : {},
                      cursor: myTeam ? 'pointer' : 'default',
                      opacity: myTeam ? 1 : 0.7
                    }}
                  >
                    <ListItemAvatar>
                      <Button onClick={() => setViewingPlayer(player)}>
                        <Avatar
                          alt={player ? `${player.first_name} ${player.last_name}` : 'Player'}
                          // @ts-ignore - image_url might exist on Player
                          src={player?.picture || undefined}
                          sx={{ width: 40, height: 40 }}
                        >
                          {player ? `${player.first_name?.charAt(0)}${player.last_name?.charAt(0)}` : ''}
                        </Avatar>
                      </Button>
                      <Box sx={{color: 'red'}}>{player?.injury?.designation}</Box>
                    </ListItemAvatar>
                    
                    <ListItemText
                      onClick={() => myTeam && handlePositionClick(playerInfoEntry, isLocked)}
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="body1" fontWeight={500} noWrap>
                            {player ? `${player.first_name} ${player.last_name}` : emptyPositionString}
                            {!!swapPlayer && <IconButton title={swapPlayer.abbr_name}>
                              <SwapHorizIcon sx={{color: "orange"}} />
                            </IconButton>}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        player ? (
                          <>
                            <Typography variant="caption" display="block">
                              {player.position} • {player.nfl_team?.alias}
                            </Typography>
                            {teamPlayer?.contract && (
                              <Typography variant="caption" display="block">
                                {toSalary(playerInfoEntry.currentYearSalary)} • {playerInfoEntry.finalYearOfContract}
                              </Typography>
                            )}
                            {(player && player.nfl_team) && 
                              <Typography variant="caption" display="block">
                                {playerMatchupDisplay(currentWeekStats, nflGame)}
                              </Typography>
                            }
                            {!!projection &&
                              <Typography variant="caption" display="block">
                                Proj. {projection}
                              </Typography>
                            }
                          </>
                        ) : null
                      }
                      sx={{ my: 0 }}
                    />
                    
                    <Box>
                      {isLocked ? (
                        <LockIcon fontSize="small" color="disabled" />
                      ) : selectedPosition ? (
                        SWAP_BUTTON_MAP.get(
                          getSwapOption(selectedPosition.split('-'), playerInfoEntry.id.split('-'), roster!, playerMap)
                        )
                      ) : null}
                      {/* {playerInfo?.score !== undefined && (
                        <Chip
                          size="small"
                          label={playerInfo.score.toFixed(1)}
                          color="primary"
                          sx={{ ml: 1 }}
                        />
                      )} */}
                    </Box>
                  </ListItem>
                  <Divider component="li" />
                </React.Fragment>
              );
            })}
          </List>
        </CardContent>
      </Collapse>
    </Card>
  );
};

const TeamRosterMobile = (props: TeamRosterMobileProps) => {
  const {
    leagueId,
    team,
    playerMap,
    currentSeasonYear,
    rosterSettings,
    myTeam
  } = props;
  
  const [selectedPosition, setSelectedPosition] = React.useState<string | undefined>(undefined);
  const [otherSelectedPosition, setOtherSelectedPosition] = React.useState<string | undefined>(undefined);
  const [roster, setRoster] = React.useState(team.playerRoster);
  const [processing, setProcessing] = React.useState(false);
  const [errors, setErrors] = React.useState<string[]>([]);
  const [isException, setIsException] = React.useState(false);
  const [isLoadComplete, setLoadComplete] = React.useState(false);
  const [showTradeDetails, setShowTradeDetails] = React.useState(false);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const user = useAppSelector((state) => state.user);
  const playerPoachInfo: PlayerPoachInfo[] | undefined = useAppSelector(
    (state) => state.playerPoachDataLoad
  )?.[team.id!];
  
  const league: League | undefined = useLoadLeagueData({
    leagueId,
    userId: user.id,
    loadBasicDraftData: false,
    loadFullDraftData: false,
    loadContractData: true,
    loadBasicAuctionData: false,
    loadFullAuctionData: false,
    loadRosterData: false,
    loadRosterFor: [],
    loadScheduleData: false,
    loadWaivedContracts: false,
    isException,
    setIsException,
    isLoadComplete,
    setLoadComplete,
  });
  
  React.useEffect(() => {
    setRoster(team.playerRoster);
  }, [team.playerRoster]);
  
  const nflGames = useAppSelector((state) => state.nflGameDataLoad);
  const nflGameMap = new Map(nflGames.map((game) => [game.id!, game]));
  
  const selectedPlayerInfo = selectedPosition ? getPlayerInfo(selectedPosition, team, roster) : undefined;
  const selectedPlayer = selectedPlayerInfo?.playerId ? playerMap.get(selectedPlayerInfo.playerId) : undefined;
  
  const otherSelectedPlayerInfo = otherSelectedPosition ? getPlayerInfo(otherSelectedPosition, team, roster) : undefined;
  const otherSelectedPlayer = otherSelectedPlayerInfo?.playerId ? playerMap.get(otherSelectedPlayerInfo.playerId) : undefined;
    
  const poachInfo: PlayerPoachInfo[] = playerPoachInfo || [];

  // Ensure league is defined before rendering the component
  if (!league) {
    return <Typography>Loading league data...</Typography>;
  }
    
  if (!rosterSettings) {
    console.error('Roster settings are required');
    return null;
  }
  
  // Get starter data
  const starterData = getStarterData(roster, poachInfo, props.currentSeasonYear, playerMap, league);
  
  // Get bench data
  let benchData = getBenchData(roster, poachInfo, starterData, rosterSettings, props.currentSeasonYear, playerMap, league);
  // TODO: Update this to take in a properties object. Makes it way less likely to fuck up the order of the input parameters
  benchData = getFilteredBenchData(
    benchData,
    selectedPosition,
    selectedPlayer,
    roster,
    playerMap,
    poachInfo,
    props.currentSeasonYear,
    league!
  );

  // Get IR data
  const irData = getIrData(roster, poachInfo, props.currentSeasonYear, playerMap, league);
  
  // Get PS data
  const psData = getPsData(team, poachInfo, props.currentSeasonYear, playerMap, league);


  // Required for the dialog
  let bid = getBid(playerPoachInfo, selectedPlayer?.id);
  const players : TradeProposalPlayer[] = getPlayersForTrade(selectedPlayer, otherSelectedPlayer, 
              selectedPosition, otherSelectedPosition, props.team, bid, playerPoachInfo);
  const maxRosterSize = getMaxTeamSize(league!);
  const teamMap = new Map([[props.team.id, props.team]]);
  const teamPlayerMap = new Map(Object.values(props.team.playerRoster!).flatMap(playerList => 
    playerList.filter(player => player).map(player => [player.playerId, player])
  ));
  
  // Get team abbreviation from player's team ID or use empty string
  const getTeamAbbr = (player: Player | undefined | null): string => {
    if (!player) return '';
    // @ts-ignore - team_id might exist on Player
    return player.team_id || player.team || '';
  };
  
  const handleActionResponse = (type: string, message: string) => {
    if (type === 'error') {
      setErrors([message]);
    } else {
      setErrors([]);
      setSelectedPosition(undefined);
      setOtherSelectedPosition(undefined);
    }
  };
  
  const handleConfirmSwap = () => {
    if (!selectedPosition || !otherSelectedPosition) return;
    
    const position1Info = selectedPosition.split('-');
    const position2Info = otherSelectedPosition.split('-');
    
    swapSpotsAfterConfirm(
      position1Info,
      position2Info,
      selectedPlayerInfo,
      otherSelectedPlayerInfo,
      team,
      getBid(playerPoachInfo, selectedPlayerInfo?.playerId),
      setRoster,
      setSelectedPosition,
      setOtherSelectedPosition,
      setErrors,
      setProcessing,
      false
    );
    
    setOtherSelectedPosition(undefined);
    setSelectedPosition(undefined);
  };
  
  const handleCancelSwap = () => {
    setSelectedPosition(undefined);
    setOtherSelectedPosition(undefined);
  };
  
  
  return (
    <Box>
      {/* Error Display */}
      {errors.length > 0 && (
        <Box mb={2} p={1} bgcolor="error.light" color="error.contrastText" borderRadius={1}>
          {errors.map((error, index) => (
            <Typography key={index} variant="body2">
              {error}
            </Typography>
          ))}
        </Box>
      )}
      <LoadingModal show={processing} />
      {/* Starters Section */}
      <PositionSection
        key="starters"
        title={`Starters (${starterData.length})`}
        data={starterData}
        onPositionSelect={setSelectedPosition}
        selectedPosition={selectedPosition}
        selectedPlayerInfo={selectedPlayerInfo}
        otherSelectedPosition={otherSelectedPosition}
        otherPlayerInfo={otherSelectedPlayerInfo}
        roster={roster}
        playerMap={playerMap}
        team={team}
        currentSeasonYear={currentSeasonYear}
        playerPoachInfo={playerPoachInfo}
        league={league}
        setRoster={setRoster}
        setSelectedPosition={setSelectedPosition}
        setOtherSelectedPosition={setOtherSelectedPosition}
        setErrors={setErrors}
        myTeam={myTeam}
      />
      
      {/* Bench Section */}
      <PositionSection
        key="bench"
        title={`Bench (${benchData.length})`}
        data={benchData}
        myTeam={myTeam}
        onPositionSelect={setSelectedPosition}
        selectedPosition={selectedPosition}
        selectedPlayerInfo={selectedPlayerInfo}
        otherSelectedPosition={otherSelectedPosition}
        otherPlayerInfo={otherSelectedPlayerInfo}
        roster={roster}
        playerMap={playerMap}
        team={team}
        playerPoachInfo={playerPoachInfo || []}
        currentSeasonYear={currentSeasonYear}
        league={league}
        setRoster={setRoster}
        setSelectedPosition={setSelectedPosition}
        setOtherSelectedPosition={setOtherSelectedPosition}
        setErrors={setErrors}
      />

      {/* IR Section */}
      <PositionSection
        key="ir"
        title={`IR (${irData.length})`}
        data={irData}
        myTeam={myTeam}
        onPositionSelect={setSelectedPosition}
        selectedPosition={selectedPosition}
        selectedPlayerInfo={selectedPlayerInfo}
        otherSelectedPosition={otherSelectedPosition}
        otherPlayerInfo={otherSelectedPlayerInfo}
        roster={roster}
        playerMap={playerMap}
        team={team}
        playerPoachInfo={playerPoachInfo || []}
        currentSeasonYear={currentSeasonYear}
        league={league}
        setRoster={setRoster}
        setSelectedPosition={setSelectedPosition}
        setOtherSelectedPosition={setOtherSelectedPosition}
        setErrors={setErrors}
      />
      
      {/* PS Section */}
      { !selectedPosition && <PositionSection
        key="ps"
        title={`PS (${psData.length})`}
        data={psData}
        myTeam={myTeam}
        onPositionSelect={setSelectedPosition}
        selectedPosition={selectedPosition}
        selectedPlayerInfo={selectedPlayerInfo}
        otherSelectedPosition={otherSelectedPosition}
        otherPlayerInfo={otherSelectedPlayerInfo}
        roster={roster}
        playerMap={playerMap}
        team={team}
        playerPoachInfo={playerPoachInfo || []}
        currentSeasonYear={currentSeasonYear}
        league={league}
        setRoster={setRoster}
        setSelectedPosition={setSelectedPosition}
        setOtherSelectedPosition={setOtherSelectedPosition}
        setErrors={setErrors}
      />
    }
      
      {/* Action Buttons */}
      {selectedPlayer && selectedPlayerInfo && (
        <Box mt={2} display="flex" gap={1} justifyContent="center">
          {getActionButtons({
            selectedPlayer,
            selectedPlayerInfo,
            league,
            team,
            currentSeasonYear,
            handleActionResponse,
            setRoster,
            selectedPosition,
            setSelectedPosition,
            commish: false,
            playerMap
          })}
          <Button
            variant="outlined"
            onClick={() => {
              setSelectedPosition(undefined);
              setOtherSelectedPosition(undefined);
            }}
          >
            Cancel
          </Button>
        </Box>
      )}
      
      {/* Confirmation Dialog */}
      <Dialog
        open={otherSelectedPosition !== undefined}
        onClose={handleCancelSwap}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Roster Move</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" alignItems="center" gap={2} my={2}>
            {selectedPlayer && (
              <Box textAlign="center">
                <Avatar
                  // @ts-ignore - image_url might exist on Player
                  src={selectedPlayer.image_url || undefined}
                  sx={{ width: 60, height: 60, mx: 'auto', mb: 1 }}
                />
                <Typography variant="subtitle1">
                  {selectedPlayer.first_name} {selectedPlayer.last_name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedPlayer.position} • {getTeamAbbr(selectedPlayer)}
                </Typography>
              </Box>
            )}
            
            <Box>
              <ArrowDownward fontSize="large" color="action" />
            </Box>
            
            {otherSelectedPlayer ? (
              <Box textAlign="center">
                <Avatar
                  // @ts-ignore - image_url might exist on Player
                  src={otherSelectedPlayer.image_url || undefined}
                  sx={{ width: 60, height: 60, mx: 'auto', mb: 1 }}
                />
                <Typography variant="subtitle1">
                  {otherSelectedPlayer.first_name} {otherSelectedPlayer.last_name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {otherSelectedPlayer.position} • {getTeamAbbr(otherSelectedPlayer)}
                </Typography>
              </Box>
            ) : (
              <Box textAlign="center">
                <Avatar sx={{ width: 60, height: 60, mx: 'auto', mb: 1 }} />
                <Typography variant="subtitle1">Empty Roster Spot</Typography>
              </Box>
            )}


            <Box sx={{ mt: 2, mb: 1 }}>
              <Typography>
                {selectedPosition?.startsWith(RosterPositionEnum.Ir) || otherSelectedPosition?.startsWith(RosterPositionEnum.Ir) ? (
                  <React.Fragment>
                    {selectedPosition?.startsWith(RosterPositionEnum.Ir) ? (
                      'Would you like to reactivate this player?'
                    ) : (
                      `Players on IR must spend at least ${MIN_WEEKS_ON_IR} weeks during the NFL Regular Season before being reactivated.`
                    )}
                  </React.Fragment>
                ) : (
                  <React.Fragment>
                    {selectedPosition?.startsWith(RosterPositionEnum.Ps) || otherSelectedPosition?.startsWith(RosterPositionEnum.Ps) ? (
                      <React.Fragment>
                        Are you sure you want to activate this player from your Practice Squad to your Active Roster for <b>1yr/{toSalary(getBid(playerPoachInfo, selectedPlayerInfo?.playerId))}</b>?
                      </React.Fragment>
                    ) : (
                      <React.Fragment>
                        Are you sure you want to make this roster move?
                      </React.Fragment>
                    )}
                  </React.Fragment>
                )}
              </Typography>
              
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Button 
                  onClick={() => setShowTradeDetails(!showTradeDetails)} 
                  size="small" 
                  sx={{ mt: 1, mb: 1 }}
                  startIcon={showTradeDetails ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                >
                  {showTradeDetails ? 'Hide details' : 'Show salary cap impact'}
                </Button>
              </Box>
              
              <Collapse in={showTradeDetails} timeout="auto" unmountOnExit>
                <Box sx={{ 
                  width: '100%',
                  maxWidth: '100%',
                  overflowX: 'auto',
                  bgcolor: 'background.paper',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  mb: 1,
                  '& > *': {
                    minWidth: 'max-content',
                    width: '100%',
                    overflow: 'visible'
                  }
                }}>
                  <TradeDetails 
                    leagueId={props.leagueId} 
                    teams={[props.team]} 
                    currentSeason={props.currentSeasonYear} 
                    players={players} 
                    picks={[]} 
                    playerMap={props.playerMap} 
                    teamMap={teamMap} 
                    teamPlayerMap={teamPlayerMap} 
                    pickMap={new Map()} 
                    salaryCap={league!.currentSeason!.nflSeason!.salaryCap!} 
                    maxRosterSize={maxRosterSize} 
                  />
                </Box>
              </Collapse>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCancelSwap} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmSwap}
            variant="contained"
            color="primary"
            autoFocus
          >
            Confirm Move
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TeamRosterMobile;
